import React from 'react';
import { ProBadge } from './ProBadge';
import { useNavigate } from '@tanstack/react-router';

export interface ColorSchemeItem {
  id: string;
  name: string;
  visual: { color: string }[];
  disabled?: boolean;
}

interface ColorSchemeSelectorProps {
  schemes: ColorSchemeItem[];
  selectedSchemeId: string | null;
  onSchemeSelect: (id: string) => void;
  title?: string;
}

export const ColorSchemeSelector: React.FC<ColorSchemeSelectorProps> = ({
  schemes,
  selectedSchemeId,
  onSchemeSelect,
  title,
}) => {
  const navigate = useNavigate();

  const handleSchemeClick = (scheme: ColorSchemeItem) => {
    if (scheme.disabled) {
      navigate({ to: '/subscription' });
    } else {
      onSchemeSelect(scheme.id);
    }
  };

  return (
    <div>
      <h2 className="text-sm font-medium mb-3">{title}</h2>
      <div className="grid grid-cols-2 gap-4">
        {schemes.map((scheme) => (
          <div
            key={scheme.id}
            className={`relative p-4 border rounded-lg transition-all cursor-pointer ${
              scheme.disabled
                ? 'border-gray-200'
                : selectedSchemeId === scheme.id
                  ? 'border-black ring-2 ring-black shadow-md'
                  : 'border-gray-300 hover:border-gray-400'
            }`}
            onClick={() => handleSchemeClick(scheme)}
            role="radio"
            aria-checked={selectedSchemeId === scheme.id}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSchemeClick(scheme);
              }
            }}
          >
            {scheme.name === 'Custom' && (
              <div className="absolute top-0 right-0 z-10">
                <ProBadge isPro={false} className="scale-75" />
              </div>
            )}
            <div className={`flex items-center justify-center mb-3 h-12 ${scheme.disabled ? 'opacity-60' : ''}`}>
              {scheme.visual.map((item, index) => {
                const zIndex = scheme.visual.length + index;

                return (
                  <div
                    key={index}
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 border-white ${
                      index > 0 ? '-ml-5' : '' // Overlap items
                    }`}
                    style={{ backgroundColor: item.color, zIndex }}
                  >
                    {/* Show sparkle icon for the last item if this is the custom scheme */}
                    {scheme.id === 'custom' && index === scheme.visual.length - 1 && (
                      <span className="text-white text-base">✨</span>
                    )}
                  </div>
                );
              })}
            </div>
            <p className={`text-center text-sm font-medium ${scheme.disabled ? 'opacity-60' : ''}`}>{scheme.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}; 